/**
 * KpaCredits - Gestion des crédits et transactions IAP de KPA Ton Concours
 * Tables : purchase_transactions, users.kpa_credits
 */
import {
  AlertCircle,
  CheckCircle,
  Coins,
  Loader2,
  RefreshCw,
  Search,
  TrendingUp,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { authFetch } from '../../services/api';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

interface PurchaseTransaction {
  id: string;
  user_id: string;
  purchase_id: string;
  product_id: string;
  platform: 'google' | 'apple';
  credits_granted: number;
  status: 'pending' | 'verified' | 'failed' | 'refunded';
  verified_at: string;
  created_at: string;
  user_display_name?: string;
  user_email?: string;
}

interface CreditStats {
  total_revenue_xof: number;
  total_transactions: number;
  verified_transactions: number;
  total_credits_sold: number;
  google_transactions: number;
  apple_transactions: number;
  revenue_by_product: { product_id: string; count: number; credits: number }[];
}

const PACK_LABELS: Record<string, string> = {
  credits_basic: '⭐ Starter (500 XOF)',
  credits_standard: '🔥 Populaire (1 500 XOF)',
  credits_premium: '👑 Meilleur Prix (3 000 XOF)',
  day_pass_24h: '🎟️ Pass Journée',
};

const getPackLabel = (productId: string) => {
  const key = Object.keys(PACK_LABELS).find(k => productId.includes(k));
  return key ? PACK_LABELS[key] : productId;
};

const STATUS_CLASSES: Record<string, string> = {
  verified: 'bg-green-100 text-green-700',
  pending: 'bg-yellow-100 text-yellow-700',
  failed: 'bg-red-100 text-red-700',
  refunded: 'bg-gray-100 text-gray-600',
};

const KpaCredits: React.FC = () => {
  const [transactions, setTransactions] = useState<PurchaseTransaction[]>([]);
  const [stats, setStats] = useState<CreditStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [txRes, statsRes] = await Promise.all([
        authFetch(`${API_BASE_URL}/api/pka/credits/transactions`),
        authFetch(`${API_BASE_URL}/api/pka/credits/stats`),
      ]);

      if (txRes.ok) {
        const data = await txRes.json();
        setTransactions(data.transactions ?? []);
      }
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const filtered = transactions.filter(tx =>
    !search || tx.product_id.includes(search) || tx.user_id.includes(search) ||
    tx.user_display_name?.toLowerCase().includes(search.toLowerCase())
  );

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-yellow-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span>💰</span>
            <h1 className="text-2xl font-bold text-gray-900">Crédits & Achats KPA</h1>
          </div>
          <p className="text-gray-500 text-sm">
            Packs crédits IAP (Google Play + App Store) — Modèle à la consommation
          </p>
        </div>
        <button onClick={loadData} className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition">
          <RefreshCw className="w-4 h-4" /> Actualiser
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2 text-sm text-red-700">
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
      )}

      {/* Modèle économique KPA */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
        <h3 className="font-semibold text-yellow-800 mb-2">💰 Modèle économique KPA Ton Concours</h3>
        <div className="grid md:grid-cols-3 gap-3 text-sm text-yellow-800">
          <div className="bg-yellow-100 rounded-lg p-3">
            <p className="font-bold">Starter</p>
            <p>500 XOF → 6 crédits</p>
            <p className="text-xs text-yellow-600">1 crédit ≈ 83 XOF</p>
          </div>
          <div className="bg-yellow-100 rounded-lg p-3">
            <p className="font-bold">Populaire</p>
            <p>1 500 XOF → 30 + 5 bonus</p>
            <p className="text-xs text-yellow-600">1 crédit ≈ 43 XOF</p>
          </div>
          <div className="bg-yellow-100 rounded-lg p-3">
            <p className="font-bold">Meilleur Prix</p>
            <p>3 000 XOF → 110 crédits</p>
            <p className="text-xs text-yellow-600">1 crédit ≈ 27 XOF</p>
          </div>
        </div>
        <p className="text-xs text-yellow-700 mt-2">
          <strong>Coûts de crédits :</strong> Sauvegarder un concours = 5 crédits • Activer le suivi = 5 crédits
        </p>
      </div>

      {/* KPI Stats */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-500">Revenus total</span>
              <TrendingUp className="w-4 h-4 text-green-500" />
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.total_revenue_xof.toLocaleString('fr-FR')} XOF</p>
          </div>
          <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-500">Transactions vérifiées</span>
              <CheckCircle className="w-4 h-4 text-blue-500" />
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.verified_transactions}</p>
            <p className="text-xs text-gray-400">sur {stats.total_transactions} total</p>
          </div>
          <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-500">Crédits vendus</span>
              <Coins className="w-4 h-4 text-yellow-500" />
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.total_credits_sold.toLocaleString('fr-FR')}</p>
          </div>
          <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-500">Plateforme</span>
              <span className="text-xs text-gray-400">Google / Apple</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.google_transactions}<span className="text-gray-400 text-lg"> / </span>{stats.apple_transactions}</p>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher par utilisateur ou produit..."
            className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-400"
          />
        </div>
      </div>

      {/* Transactions table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h2 className="font-semibold text-gray-800">Transactions récentes</h2>
          <span className="text-sm text-gray-400">{filtered.length} transactions</span>
        </div>
        {filtered.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            <Coins className="w-8 h-8 mx-auto mb-2" />
            <p>Aucune transaction trouvée</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 text-gray-600 font-medium">Utilisateur</th>
                  <th className="text-left px-4 py-3 text-gray-600 font-medium">Pack</th>
                  <th className="text-center px-4 py-3 text-gray-600 font-medium">Crédits</th>
                  <th className="text-center px-4 py-3 text-gray-600 font-medium">Plateforme</th>
                  <th className="text-center px-4 py-3 text-gray-600 font-medium">Statut</th>
                  <th className="text-left px-4 py-3 text-gray-600 font-medium">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((tx) => (
                  <tr key={tx.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900">{tx.user_display_name || tx.user_id.slice(0, 8)}</p>
                      <p className="text-xs text-gray-400 truncate max-w-[160px]">{tx.user_email}</p>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{getPackLabel(tx.product_id)}</td>
                    <td className="px-4 py-3 text-center">
                      <span className="font-bold text-yellow-600">+{tx.credits_granted}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${tx.platform === 'google' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                        {tx.platform === 'google' ? '▶ Google' : '🍎 Apple'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_CLASSES[tx.status]}`}>
                        {tx.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-400">{formatDate(tx.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default KpaCredits;
